import { useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import type { ConfigPreset, ConfigPresetMode } from '../types/testdata.types'
import type { UseConfigPresetsReturn } from '../hooks/useConfigPresets'

const inputCls = 'rounded-lg border border-purple-300 dark:border-purple-700 bg-white dark:bg-gray-700 text-sm text-gray-900 dark:text-gray-100 px-2 py-1.5 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-colors'

const MODE_LABEL_KEYS: Record<ConfigPresetMode, string> = {
  single: 'config_presets.mode_single',
  set: 'config_presets.mode_set',
  debit_credit_set: 'config_presets.mode_debit_credit_set',
}

interface Props {
  hook: UseConfigPresetsReturn
  currentMode: ConfigPresetMode
  buildConfig: () => Record<string, unknown>
  onLoad: (preset: ConfigPreset) => void
}

/** "Testscenario's" — volledige configuratie-snapshots, expliciet los van de
 * bestaande "Sjablonen" (die alleen partij-/regelgegevens per documenttype opslaan). */
export function ConfigPresetsPanel({ hook, currentMode, buildConfig, onLoad }: Props) {
  const { t } = useTranslation()

  useEffect(() => { hook.loadPresets() }, [hook.loadPresets])

  return (
    <div className="bg-purple-50 dark:bg-purple-900/10 border border-purple-100 dark:border-purple-800/40 rounded-xl p-4">
      <h3 className="text-xs font-semibold text-purple-700 dark:text-purple-300 uppercase tracking-wide mb-1">
        {t('config_presets.title')}
      </h3>
      <p className="text-xs text-purple-600/80 dark:text-purple-400/80 mb-3">{t('config_presets.subtitle')}</p>

      {hook.presets.length > 0 && (
        <div className="flex flex-wrap gap-2 mb-3">
          {hook.presets.map(p => (
            <div key={p.id} className="flex items-center gap-1 bg-white dark:bg-gray-700 border border-purple-200 dark:border-gray-600 rounded-lg px-2 py-1">
              <button
                onClick={() => onLoad(p)}
                className="text-xs text-purple-700 dark:text-purple-300 hover:underline font-medium"
                title={t(MODE_LABEL_KEYS[p.mode])}
              >
                {p.name}
              </button>
              <span className="text-xs text-gray-400">({t(MODE_LABEL_KEYS[p.mode])})</span>
              <button
                onClick={() => hook.deletePreset(p.id)}
                disabled={hook.deletingId === p.id}
                className="ml-1 text-gray-400 hover:text-red-500 text-xs leading-none"
                title={t('common.delete')}
              >
                {hook.deletingId === p.id ? '…' : '×'}
              </button>
            </div>
          ))}
        </div>
      )}

      <div className="flex flex-wrap items-center gap-2">
        <input
          className={inputCls}
          value={hook.name}
          onChange={e => hook.setName(e.target.value)}
          placeholder={t('config_presets.name_placeholder')}
        />
        <button
          onClick={() => hook.savePreset(currentMode, buildConfig())}
          disabled={hook.saving}
          className="px-3 py-1.5 bg-purple-600 hover:bg-purple-700 disabled:bg-purple-300 text-white text-xs font-semibold rounded-lg transition-colors"
        >
          {hook.saving ? t('common.busy') : t('config_presets.save_button')}
        </button>
      </div>
      {hook.error && (
        <p className="mt-2 text-xs text-red-500 dark:text-red-400">{hook.error}</p>
      )}
    </div>
  )
}
